import { useStore } from 'effector-react';
import { ErrorLayout } from 'features/fallback/ui/error-layout';
import { useEffect } from 'preact/hooks';
import { FunctionalComponent, h } from 'preact';

import { not } from 'libs/bool';
import { Logo } from 'ui/logo';
import { Subtitle } from 'ui/typography';
import { ExitPopup } from 'ui/exit-popup';
import { mountType$, params$ } from 'model/main.model';
import { $route, navigate, Route } from 'libs/router';
import { isReady$ } from 'model/loading.model';
import { errorType$ } from 'model/error.model';
import { LanguagePopup } from 'ui/language-popup';
import { routes } from 'features/fallback/routes';
import { ErrorDetails, isIntentParams, MountMode, MountType } from 'model/domain';
import { Spinner, SpinnerContainer } from 'ui/spinner';
import { LinkView } from 'features/fallback/features/link/link';
import { FallbackError } from 'features/fallback/features/error';
import { WelcomeView } from 'features/fallback/features/welcome';
import { FallbackContainer } from 'features/fallback/ui/fallback-container';
import { PhoneConnectedView } from 'features/fallback/features/phone-connected';
import {
	closeLocalizationPopup,
	isLocalizationPopupOpen$,
} from 'model/localization.model';
import {
	closeExitPopup,
	connectFallbackSocket,
	fallbackCancel,
	isFallbackExit$,
} from 'model/flows/fallback.model';

export const Fallback: FunctionalComponent<{ mode: MountMode, error?: ErrorDetails }> = (props) => {
	const { mode } = props;
	const mountType = useStore(mountType$);
	const isReady = useStore(isReady$);
	const isExit = useStore(isFallbackExit$);
	const isLocalization = useStore(isLocalizationPopupOpen$);
	const errorType = useStore(errorType$);
	const params = useStore(params$);
	const route = useStore($route);

	useEffect(() => {
		if (mountType === MountType.fallback) {
			connectFallbackSocket();
			navigate(routes.welcome);
		}
		if (props.error) {
			navigate(routes.error.path);
		}
	}, []);

	if (props.error) {
		return (
			<FallbackContainer mode={mode}>
				{isIntentParams(params) && (
					<Logo
						src={params.options.intent.logo}
					/>
				)}
				<ErrorLayout
					title={props.error.title}
					description={props.error.description}
				/>
			</FallbackContainer>
		);
	}

	if (not(isReady)) {
		return (
			<FallbackContainer mode={mode}>
				<SpinnerContainer>
					<Spinner
						size={84}
						fill={{
							primary: 'var(--primary-color)',
							secondary: 'var(--secondary-color)',
						}}
					/>
					<Subtitle style={{
						marginTop: 42,
						textAlign: 'center',
					}}>
						Putting everything together
					</Subtitle>
				</SpinnerContainer>
			</FallbackContainer>
		);
	}

	return (
		<FallbackContainer mode={mode}>
			{route === routes.welcome && isIntentParams(params) && (
				<Logo
					src={params.options.intent.logo}
				/>
			)}
			<Route path={routes.welcome} component={<WelcomeView/>}/>
			<Route path={routes.connected} component={<PhoneConnectedView/>}/>
			<Route path={routes.link} component={<LinkView/>}/>
			<Route path={routes.error.path} component={<FallbackError/>}/>
			<ExitPopup
				overlay={!isIntentParams(params)}
				visible={isExit}
				onClose={() => closeExitPopup()}
				onExit={() => {
					fallbackCancel(errorType);
				}}
			/>
			<LanguagePopup
				visible={isLocalization}
				onClose={() => closeLocalizationPopup()}
			/>
		</FallbackContainer>
	);
};
